import { SendOutlined } from '@ant-design/icons';
import { SmartReplyButton } from '@copilot/common/components/forms/messageForm/smartReply/smartReplyButton';
import { Features } from '@copilot/data/responses/interface';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { Alert, Button, Flex, Input, Space } from 'antd';
import {
    OperationStatuses,
    StatusIndicator,
} from '@copilot/common/components/statusIndicator/statusIndicator';
import styles from './card.module.less';
import {
    linkedInDisconnectedMsg,
    linkedInRestrictedMsg,
    manualMessageDisabledMsg,
} from '@copilot/common/constant/strings';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import { INBOX_SEND_MESSAGE_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { useSmartReplyManagement } from '../../../../hooks/smartReply/useSmartReplyManagement';
import { PromptSubmissionData } from '@copilot/common/hooks/smartReply/smartReplyTypes';
import { InboxMessageType } from '@copilot/common/pages/inbox/data/types';
import { RichTextEditor } from '@copilot/common/components/editors/richTextEditor/richTextEditor';
import { MessageEditorNameTokens } from '@copilot/common/utils/constant';
import { RichTextEditorRef } from '@copilot/common/components/editors/richTextEditor/types/richTextEditorTypes';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import { RecommendedMessageView } from '@copilot/common/components/editors/recommendedMessage/view';
import { useReplyRecommendation } from '@copilot/common/hooks/replyRecommendation/useReplyRecommendation';
import { useRecommendedMessageFeedback } from '@copilot/common/hooks/replyRecommendation';
import AutoReplyStatusIndicator from '../autoReply/autoReplyStatusIndicator';
import isEqual from 'lodash/isEqual';
import { useAutoReplyToggle } from '@copilot/common/hooks/replyRecommendation/useAutoReply';
import notificationManager from '@copilot/common/utils/notificationManager';

/**
 * Props for the smart text editor
 */
type SmartTextEditorProps = {
    /**
     * Handler for slicking send
     * @param text The text to send
     * @param prompt The prompt used to generate the message (if any)
     * @returns
     */
    onSubmit: (text: string, prompt?: PromptSubmissionData) => void;
    /**
     * Whether we are in the loading / sending state
     */
    isLoading: boolean;
    /**
     *
     */
    message: InboxMessageType;
    /**
     * Whether we should show reply recommendations or not
     */
    isReplyRecommendationsEnabled: boolean;
};

/**
 * Text editor integrated to use SmartReply
 * @returns
 */
export default function SmartTextEditor({
    onSubmit: handleSubmit,
    isLoading,
    message,
    isReplyRecommendationsEnabled,
}: SmartTextEditorProps) {
    const [recommendedReply, setRecommendedReply] = useState<string>(
        message.replyRecommendation?.message ?? ''
    );
    const recommendedFeedback = message.replyRecommendation?.feedback?.rating;

    const [msgText, setMsgText] = useState<string>(recommendedReply ?? '');
    const [isEditorActive, setIsEditorActive] = useState<boolean>(false);
    const { isLoggedIn, isConversationRestricted, isLiSessionInRetryState } = message;
    const isManualSendMessageFeatureEnabled = useFeatureToggle(Features.ManualSendMessageFeature);
    const { isRichTextEditorEnabled, isAutoReplyBetaV1Enabled } = useFeatureFlags();
    const { onUpdateFeedback } = useRecommendedMessageFeedback();
    const hasValidMessage = !isEmpty(msgText.trim());
    const {
        smartReplyResult,
        lastUsedPrompt,
        isAssistantActive,
        setIsAssistantActive,
        hasGenerated,
        regenerateSmartReply,
        smartReplyPromptCategory,
        isStatusVisible,
        operationStatus,
        operationMessage,
        onSelectPreset,
        onSubmitCustomPrompt,
        canUseCommunicationStyles,
    } = useSmartReplyManagement(msgText, message);
    const editorRef = useRef<RichTextEditorRef>(null);

    const { regenerateMessage, isRegenerating } = useReplyRecommendation({
        orgMemberId: message.orgMemberId,
        sourceProfileId: message.orgMemberProfileId,
        targetProfileId: message.linkedInProfileId,
    });

    async function onRegenerateRecommendedMessage() {
        const newText = await regenerateMessage();
        if (newText) {
            setRecommendedReply(newText);
            setMsgText(newText);
        }
        return !isEmpty(newText);
    }

    function onSubmit() {
        const prompt =
            !isNil(smartReplyResult.data) && msgText === smartReplyResult.data
                ? lastUsedPrompt
                : undefined;
        handleSubmit(msgText, prompt);
    }

    const isHardLoggedOut = !isLoggedIn && !isLiSessionInRetryState;

    function calcDisabledValue() {
        if (!isManualSendMessageFeatureEnabled) {
            return manualMessageDisabledMsg;
        } else if (isHardLoggedOut) {
            return linkedInDisconnectedMsg;
        } else if (isConversationRestricted) {
            return linkedInRestrictedMsg;
        } else {
            return '';
        }
    }

    const isDisabled =
        isHardLoggedOut ||
        isLoading ||
        isConversationRestricted ||
        !isManualSendMessageFeatureEnabled;

    const isSmartReplyGenerating = operationStatus === OperationStatuses.inProgress;

    useEffect(() => {
        if (isNil(smartReplyResult.data)) return;
        setMsgText(smartReplyResult.data);
        setIsEditorActive(false);
    }, [smartReplyResult.data]);

    useLayoutEffect(() => {
        if (isEditorActive) {
            editorRef.current?.focus();
        }
    }, [isEditorActive]);

    useEffect(() => {
        const shouldClearReplyRecommendation =
            !isReplyRecommendationsEnabled && msgText === recommendedReply;
        const shouldSetReplyRecommendation =
            isReplyRecommendationsEnabled && msgText === '' && !isEditorActive;

        if (shouldClearReplyRecommendation) {
            setMsgText('');
        } else if (shouldSetReplyRecommendation) {
            setMsgText(recommendedReply ?? '');
        }
    }, [isReplyRecommendationsEnabled, recommendedReply, msgText]);

    const shouldShowRecommendedState =
        isReplyRecommendationsEnabled &&
        !isDisabled &&
        !isEditorActive &&
        !isEmpty(recommendedReply) &&
        msgText === recommendedReply;

    const {
        isPaused: isAutoReplyPaused,
        isEnabled: isAutoReplyEnabled,
        isUpdating: isAutoReplyUpdating,
        isIndicatorShown: isAutoReplyToggleShown,
        requirePopconfirm,
        onToggle: onToggleAutoReply,
    } = useAutoReplyToggle({
        isEnabledForCampaign: message.autoReply.isEnabledForCampaign,
        isEnabledForConnection: message.autoReply.isEnabledForConnection,
        isLastMessageReceived: isEqual(message.lastReceived, message.lastMessage),
        autoReplyStatus: message.replyRecommendation?.metaData?.autoReplyStatus,
    });

    async function handleToggleAutoReply(isEnabled: boolean) {
        if (!requirePopconfirm || !isEnabled) {
            // Turn on auto reply without sending the message if popconfirm is not required
            // Also allow the user to turn off auto reply without sending the message
            onToggleAutoReply(message.connectionId, isEnabled);
            return;
        }

        if (isEmpty(msgText)) {
            notificationManager.showErrorNotification({
                message: 'Could not resume Auto Reply',
                description: 'Please provide a message in the text box to resume Auto Reply.',
            });
            return;
        }

        // Send the message showing send UX to the user before enabling auto reply
        onSubmit();
        await onToggleAutoReply(message.connectionId, isEnabled);
    }

    return (
        <Flex vertical gap="small">
            {isStatusVisible === true && !isSmartReplyGenerating && (
                <StatusIndicator status={operationStatus}>{operationMessage}</StatusIndicator>
            )}
            {!isStatusVisible && hasGenerated ? (
                <Alert
                    className={styles.reviewResponseAlert}
                    type="info"
                    message="Review the AI response for accuracy."
                    showIcon
                />
            ) : null}
            <Flex vertical className={styles.editorWrapper}>
                <div className={styles.editorContainer}>
                    {shouldShowRecommendedState && (
                        <RecommendedMessageView
                            message={recommendedReply ?? ''}
                            feedback={recommendedFeedback}
                            onClick={() => {
                                setIsEditorActive(true);
                            }}
                            onFeedbackChange={(feedbackType, feedbackComment) => {
                                onUpdateFeedback(message.threadId, feedbackType, feedbackComment);
                            }}
                            onRegenerate={onRegenerateRecommendedMessage}
                            onDelete={() => {
                                setMsgText('');
                                setIsEditorActive(true);
                            }}
                            isRegenerating={isRegenerating}
                        />
                    )}
                    {!shouldShowRecommendedState && isRichTextEditorEnabled && (
                        <RichTextEditor
                            multiline
                            value={msgText}
                            tokens={MessageEditorNameTokens.map((token) => token.value)}
                            placeholder={'Write a reply...'}
                            onChange={(value) => {
                                setMsgText(value);
                                setIsEditorActive(true);
                            }}
                            disabled={isDisabled}
                            disabledValue={calcDisabledValue()}
                            size={'small'}
                            resize={false}
                            ref={editorRef}
                            onBlur={() => setIsEditorActive(false)}
                            bordered={false}
                        />
                    )}
                    {!shouldShowRecommendedState && !isRichTextEditorEnabled && (
                        <Input.TextArea
                            autoSize={{ minRows: 1, maxRows: 8 }}
                            placeholder={isDisabled ? calcDisabledValue() : 'Write a reply'}
                            onChange={(e) => {
                                setMsgText(e.target.value);
                                setIsEditorActive(true);
                            }}
                            disabled={isDisabled}
                            value={msgText}
                        />
                    )}
                </div>
                <Flex
                    className={styles.editorActionsContainer}
                    justify="space-between"
                    align="center"
                >
                    <div>
                        {isStatusVisible === true && isSmartReplyGenerating && (
                            <StatusIndicator
                                className={styles.aiIsWritingIndicator}
                                status={operationStatus}
                            >
                                {operationMessage}
                            </StatusIndicator>
                        )}
                        {!smartReplyResult.loading && (
                            <SmartReplyButton
                                hasMessage={hasValidMessage}
                                setIsActive={setIsAssistantActive}
                                isActive={isAssistantActive}
                                canRegenerate={hasGenerated}
                                onRegenerate={regenerateSmartReply}
                                onSubmitCustomPrompt={(...args) => {
                                    onSubmitCustomPrompt(...args);
                                }}
                                onSelectPreset={(...args) => {
                                    onSelectPreset(...args);
                                }}
                                disabled={isDisabled}
                                smartReplyPromptCategory={smartReplyPromptCategory}
                                canUseCommunicationStyles={canUseCommunicationStyles}
                            />
                        )}
                    </div>
                    <Space align="center" size={16}>
                        {isAutoReplyBetaV1Enabled && isAutoReplyToggleShown && (
                            <AutoReplyStatusIndicator
                                isEnabled={isAutoReplyEnabled}
                                isPaused={isAutoReplyPaused}
                                onChange={handleToggleAutoReply}
                                isUpdating={isAutoReplyUpdating}
                                usePopconfirm={requirePopconfirm}
                            />
                        )}
                        <Button
                            icon={<SendOutlined />}
                            type={'primary'}
                            onClick={onSubmit}
                            disabled={!hasValidMessage || isDisabled}
                            loading={isLoading}
                            data-tracking-id={INBOX_SEND_MESSAGE_TRACKING_ID}
                        >
                            {shouldShowRecommendedState ? 'Send AI Message' : 'Send'}
                        </Button>
                    </Space>
                </Flex>
            </Flex>
        </Flex>
    );
}
